/**
 * Shared rule-set helpers for Page Builder elements.
 *
 * `ruleAccess` is a map of `{ [rule_set_id]: boolean }` built once in PageViewer,
 * after the server has evaluated the signed-in user's Keap tags. Until that lands,
 * a lookup returns `undefined`.
 *
 * 🔴 `undefined` means "we do not know yet" — it does NOT mean "denied". Collapsing
 * the two is what sent entitled, paying members to the upsell page: the verdict
 * requires a live, uncached, un-timed-out call out to api.infusionsoft.com, so the
 * unknown window is routinely seconds long — and every gated tile is clickable for
 * all of it. Never act on a permission without first checking the verdict is in.
 *
 * These live in one module because the same five lines were previously copy-pasted
 * into Card.js, Button.js and Image.js, which is how the three drifted apart.
 */

/** A tile is gated only when it names a real rule set. `-1` and falsy both mean "open to all". */
export const isGated = (details) =>
  !!details && !!details.rule_set && details.rule_set !== -1;

/**
 * `true`/`false` once known, `undefined` while the verdict is still outstanding.
 * Ungated tiles are always `true` — they never wait on anything, so pages with no
 * gating are completely unaffected by any of this.
 */
export const permissionFor = (details, ruleAccess) => {
  if (!isGated(details)) return true;
  return ruleAccess ? ruleAccess[details.rule_set] : undefined;
};

/**
 * True while a gated element must not be interactive, because we cannot yet tell
 * "allowed" from "denied". Ungated elements are never awaiting anything.
 */
export const isAwaitingVerdict = (details, ruleAccessReady) =>
  isGated(details) && !ruleAccessReady;
